import ThemeToggle from '@components/Theme/ThemeToggle';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import {
	alpha,
	Box,
	Button,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	Typography
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from 'hooks/useAuth';
import React, { FC, useCallback, useContext } from 'react';
import { IThemeChangeProps } from 'types/theme';
import { useBoolean } from 'usehooks-ts';
import { getUserDisplayName } from 'utils/users';
import PromoteBiometricsDialog from './PromoteBiometricsDialog';

const UserButtonsMenu: FC<IThemeChangeProps> = ({
	isDarkTheme,
	onThemeChange
}) => {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const { consoleUser, logout } = useContext(AuthContext);
	const { value: promoteDialogOpen, setFalse, setTrue } = useBoolean(false);
	const queryClient = useQueryClient();

	const open = Boolean(anchorEl);

	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleCloseMenu = useCallback(() => {
		setAnchorEl(null);
	}, [setAnchorEl]);

	const handleOpenPromoteBiometrics = useCallback(() => {
		setAnchorEl(null);
		setTrue();
	}, [setTrue, setAnchorEl]);

	const handleLogout = useCallback(() => {
		handleCloseMenu();
		logout();
		queryClient.removeQueries(); // clear all queries
	}, [handleCloseMenu, queryClient, logout]);

	return (
		<Box>
			{promoteDialogOpen && (
				<PromoteBiometricsDialog
					onSuccess={setFalse}
					onCancel={setFalse}
					onError={setFalse}
				/>
			)}
			<Button
				onClick={handleOpenMenu}
				data-testid="user-pref-dialog"
				aria-label={getUserDisplayName(consoleUser)}
				color="inherit"
				variant="contained"
				disableElevation
				size="small"
				sx={{
					backgroundColor: 'transparent',
					'&:hover': {
						backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2)
					},
					'&:active': {
						backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1)
					}
				}}
			>
				<AccountCircleOutlinedIcon
					fontSize="small"
					sx={{
						color: 'text.secondary'
					}}
				/>
				<Typography
					variant="body2"
					textOverflow="ellipsis"
					overflow="hidden"
					color="text.secondary"
					pl={1}
					display={{
						xs: 'none',
						sm: 'inherit'
					}}
				>
					{getUserDisplayName(consoleUser)}
				</Typography>
			</Button>
			<Menu
				id="basic-menu"
				data-testid="user-buttons-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleCloseMenu}
				MenuListProps={{ sx: { pb: 0 } }}
			>
				<Box px={1}>
					<Box width={180} display="flex" flexGrow={1} pl={1}>
						<ThemeToggle
							onThemeChange={onThemeChange}
							isDarkTheme={isDarkTheme}
						/>
					</Box>
					<List>
						<ListItem
							onClick={handleOpenPromoteBiometrics}
							data-testid="open-promote-biometrics"
							disablePadding
						>
							<ListItemButton>
								<ListItemIcon
									sx={{
										minWidth: 24
									}}
								>
									<FingerprintIcon
										fontSize="small"
										sx={{
											color: 'text.secondary'
										}}
									/>
								</ListItemIcon>
								<ListItemText
									primary={
										<Typography color="text.secondary" variant="body2" pl={1}>
											{intl.tr('pages.promote.title')}
										</Typography>
									}
								/>
							</ListItemButton>
						</ListItem>

						<ListItem
							onClick={handleLogout}
							disablePadding
							data-testid="ncm-button"
						>
							<ListItemButton>
								<ListItemIcon
									sx={{
										minWidth: 24
									}}
								>
									<LogoutOutlinedIcon
										fontSize="small"
										sx={{
											color: 'text.secondary'
										}}
									/>
								</ListItemIcon>
								<ListItemText
									primary={
										<Typography color="text.secondary" variant="body2" pl={1}>
											{intl.tr('pages.logout.title')}
										</Typography>
									}
								/>
							</ListItemButton>
						</ListItem>
					</List>
				</Box>
			</Menu>
		</Box>
	);
};

export default UserButtonsMenu;
